import { useCallback } from "react"
import { useChatComposer, useChatStore } from "@mui/x-chat-headless"
import { tagAttachMethod } from "./attach-method"
import type { AttachMethod } from "./attach-method"
import { useAttachmentsConfig } from "./attachments-config"
import type { AttachmentRejection } from "./attachments-config"

function matchesAcceptedMimeType(mimeType: string, acceptedMimeTypes: readonly string[]): boolean {
  return acceptedMimeTypes.some((accepted) => (accepted.endsWith("/*") ? mimeType.startsWith(accepted.slice(0, -1)) : mimeType === accepted))
}

export type AcceptAttachments = (files: readonly File[], method: AttachMethod, folders?: readonly File[]) => void

export function useAcceptAttachments(): AcceptAttachments {
  const { addAttachment } = useChatComposer()
  const store = useChatStore()
  const { acceptedMimeTypes, maxFileSize, maxFileCount, onAttachmentReject } = useAttachmentsConfig()

  return useCallback((files, method, folders = []) => {
    const rejections: AttachmentRejection[] = folders.map((file) => ({ file, reason: "folder" }))
    const accepted: File[] = []
    const queuedCount = store.state.composerAttachments.length
    for (const file of files) {
      if (acceptedMimeTypes != null && acceptedMimeTypes.length > 0 && !matchesAcceptedMimeType(file.type, acceptedMimeTypes)) {
        rejections.push({ file, reason: "mime-type" })
      } else if (maxFileSize != null && file.size > maxFileSize) {
        rejections.push({ file, reason: "file-size" })
      } else if (maxFileCount != null && queuedCount + accepted.length >= maxFileCount) {
        rejections.push({ file, reason: "file-count" })
      } else {
        accepted.push(file)
      }
    }
    if (rejections.length > 0) onAttachmentReject?.(rejections)
    for (const file of accepted) {
      tagAttachMethod(file, method)
      addAttachment(file)
    }
  }, [addAttachment, store, acceptedMimeTypes, maxFileSize, maxFileCount, onAttachmentReject])
}
